/** THE MANUAL — the list. One big row per function, with a search box. */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight, Search } from 'lucide-react';
import api from '../../lib/api';

const ManualList = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [find, setFind] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/manual/sections')
            .then((res) => setRows(res.data.sections || []))
            .finally(() => setLoading(false));
    }, []);

    const shown = useMemo(() => {
        const q = find.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => `${r.title} ${r.summary} ${r.where}`
            .toLowerCase().includes(q));
    }, [rows, find]);

    return (
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="manual-list-page">
            <div className="flex items-center gap-3 mb-2">
                <BookOpen className="w-7 h-7 text-emerald-500" aria-hidden="true" />
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Manual</h1>
            </div>
            <p className="text-base text-slate-500 mb-7">
                Step by step, in plain words. Open one, send it to anyone.
            </p>

            <div className="relative mb-6">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        aria-hidden="true" />
                <input value={find} onChange={(e) => setFind(e.target.value)}
                       placeholder="Search the manual…"
                       data-testid="manual-search"
                       className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 pl-9 pr-3 py-3 text-base" />
            </div>

            {loading && <p className="text-sm text-slate-400">Loading…</p>}

            <div className="space-y-3">
                {shown.map((row) => (
                    <button key={row.id} type="button"
                            onClick={() => navigate(`/manual/${row.id}`)}
                            data-testid={`manual-row-${row.id}`}
                            className="w-full text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-5 hover:border-emerald-500 hover:shadow-lg transition-all duration-200 flex items-center gap-4">
                        <span className="flex-1">
                            <span className="block text-lg font-semibold">{row.title}</span>
                            <span className="block text-sm text-slate-500 mt-0.5">{row.summary}</span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" aria-hidden="true" />
                    </button>
                ))}
                {!loading && !shown.length && (
                    <p className="text-sm text-slate-400" data-testid="manual-no-match">
                        Nothing matches that word.
                    </p>
                )}
            </div>
        </div>
    );
};

export default ManualList;
